/* src/components/LibraryView.js */
import React, { useState, useMemo } from 'react';
import OnboardingTooltip from './OnboardingTooltip';
import './LibraryView.css';

const SKILL_SQUARE_WIDTH = 24; 

const LibraryView = ({ 
    allSubjectsData, 
    allItemsMap, 
    subjectMapDerived, 
    setSubject, 
    setView, 
    userAccessLevel, 
    onOpenUpgrade, 
    rulesProgress, 
    onOpenRule,
    seenHints,
    onDismissHint
}) => {
    const [filter, setFilter] = useState('all');
    const [sortBy, setSortBy] = useState('az');
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

    const isFree = userAccessLevel === 'free';

    const searchResults = useMemo(() => {
        const query = searchTerm.toLowerCase().trim();
        if (!query) return [];
        
        const tokens = query.split(/\s+/);
        const results = [];
        
        allItemsMap.forEach((item) => {
            if (item.itemType !== 'rule') return;
            
            const keynote = rulesProgress?.[item.id]?.keynote?.toLowerCase() || '';
            const name = item.ruleName.toLowerCase();
            const content = item.content_plain?.toLowerCase() || '';
            
            const searchStack = `${name} ${keynote} ${content}`;
            
            if (tokens.every(token => searchStack.includes(token))) {
                results.push(item);
            }
        });

        return results.sort((a, b) => a.ruleName.length - b.ruleName.length).slice(0, 15);
    }, [searchTerm, allItemsMap, rulesProgress]);

    const handleResultClick = (item) => {
        // LOCK LOGIC: Prevent opening if user is free and rule is not a preview
        if (isFree && !item.is_free_preview) {
            onOpenUpgrade();
            return;
        }
        setIsSearchModalOpen(false);
        setSearchTerm('');
        if (onOpenRule) onOpenRule(item.id);
    };

    const calculateSubunitStatus = (rule_ids) => {
        if (!rule_ids || rule_ids.length === 0) return 'not_started';
        const statuses = rule_ids
            .map(id => allItemsMap.get(id))
            .filter(item => item && item.itemType === 'rule')
            .map(item => item.status || 'not_started');

        if (statuses.length === 0) return 'not_started'; 
        const isAllMastered = statuses.every(s => s === 'mastered');
        if (isAllMastered) return 'mastered';
        const isAllNotStarted = statuses.every(s => s === 'not_started');
        if (isAllNotStarted) return 'not_started';
        return 'in_progress';
    };

    const libraryData = useMemo(() => {
        const subjects = [];

        for (const subjectKey in allSubjectsData) {
            const subjectName = subjectMapDerived[subjectKey];
            if (!subjectName) continue;

            const items = allSubjectsData[subjectKey];
            const hasFreeContent = items.some(item => item.is_free_preview === true);
            const isPremium = userAccessLevel === 'cycle' || userAccessLevel === 'lifetime';
            const isLocked = !isPremium && !hasFreeContent;
            
            const totalRules = items.filter(i => i.itemType === 'rule').length;
            const masteredRules = items.filter(i => i.itemType === 'rule' && i.status === 'mastered').length;

            const subjectObj = {
                id: subjectKey,
                name: subjectName,
                isLocked,
                type: ['torts', 'contracts', 'property', 'civpro', 'evidence', 'criminal', 'constitution'].includes(subjectKey) ? 'MBE' : 'MEE',
                masteryPct: totalRules > 0 ? (masteredRules / totalRules) * 100 : 0,
                units: [],
            };

            const unitsMap = new Map();

            items.forEach(item => {
                const path = item.displayPath && item.displayPath.length > 0 ? item.displayPath : ['Uncategorized'];
                const unitTitle = path[0];
                let subunitTitle = (path.length > 1) ? path[1] : (item.ruleName || 'General');

                if (!unitsMap.has(unitTitle)) unitsMap.set(unitTitle, { title: unitTitle, subunits: new Map() });
                const unit = unitsMap.get(unitTitle);

                if (!unit.subunits.has(subunitTitle)) unit.subunits.set(subunitTitle, { title: subunitTitle, rule_ids: [], quiz_ids: [] });
                const subunit = unit.subunits.get(subunitTitle);
                
                if (item.itemType === 'rule') subunit.rule_ids.push(item.id);
                else if (item.itemType === 'quiz') subunit.quiz_ids.push(item.id);
            });
            
            unitsMap.forEach(unitData => {
                const unitObj = { title: unitData.title, subunits: [] };
                unitData.subunits.forEach(subunitData => {
                    unitObj.subunits.push({
                        title: subunitData.title,
                        rule_ids: subunitData.rule_ids,
                        quiz_ids: subunitData.quiz_ids,
                        all_ids: [...subunitData.rule_ids, ...subunitData.quiz_ids],
                    });
                });
                subjectObj.units.push(unitObj);
            });
            subjects.push(subjectObj);
        }
        
        return subjects
            .filter(s => filter === 'all' || s.type.toLowerCase() === filter)
            .sort((a, b) => (sortBy === 'az' ? a.name.localeCompare(b.name) : b.masteryPct - a.masteryPct));

    }, [allSubjectsData, subjectMapDerived, userAccessLevel, allItemsMap, filter, sortBy]);
    
    const handleSubjectClick = (subject) => {
        if (subject.isLocked) onOpenUpgrade();
        else {
            setSubject(subject.id);
            setView('corpus');
        }
    };

    return (
        <div className="library-view-container theme-aware">
            <header className="library-header-section">
                <h2 className="library-title">Corpus Map</h2>
                <p className="library-subtitle">A bird's-eye view of your progress. Each block represents a group of rules.</p>
            </header>

            <div className="library-toolbar">
                <div className="filter-group">
                    <span className="toolbar-label">Filter:</span>
                    <button className={`tool-btn ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>All</button>
                    <button className={`tool-btn ${filter === 'mbe' ? 'active' : ''}`} onClick={() => setFilter('mbe')}>MBE</button>
                    <button className={`tool-btn ${filter === 'mee' ? 'active' : ''}`} onClick={() => setFilter('mee')}>MEE</button>
                </div>
                <div className="sort-group">
                    <span className="toolbar-label">Sort:</span>
                    <select className="tool-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                        <option value="az">A-Z</option>
                        <option value="progress">Mastery %</option>
                    </select>
                </div>
            </div>

            <div className="command-search-container" style={{marginBottom: '20px'}}>
                <form className="search-box-full" onSubmit={(e) => { e.preventDefault(); if (searchTerm) setIsSearchModalOpen(true); }}>
                    <input 
                        type="text" 
                        className="search-input" 
                        placeholder="Search rules, contents, or keynotes..." 
                        value={searchTerm} 
                        onChange={(e) => setSearchTerm(e.target.value)} 
                    />
                    <button type="submit" className="search-btn">
                        <span className="material-symbols-outlined">search</span>
                    </button>
                </form>
            </div>

            <div className="subject-row-container">
                {libraryData.map(subject => (
                     <div key={subject.id} className={`subject-row ${subject.isLocked ? 'locked' : ''}`} onClick={() => handleSubjectClick(subject)}>
                        <div className="subject-row-content">
                            <div className="subject-header-group">
                                <h4 className="subject-grid-header">
                                    {subject.name}
                                    {subject.isLocked && <span className="material-symbols-outlined lock-icon">lock</span>}
                                </h4>
                                <div className={`subject-type-badge badge-${subject.type}`}>{subject.type}</div>
                            </div>
                            <div className={`subject-skills-wrapper ${subject.isLocked ? 'blurred-content' : ''}`}>
                                {subject.units.flatMap(unit => unit.subunits).map((subunit, index) => {
                                    const status = calculateSubunitStatus(subunit.rule_ids);
                                    const blockWidth = Math.max(SKILL_SQUARE_WIDTH, subunit.all_ids.length * 12);
                                    
                                    const blockItem = (
                                        <div 
                                            key={index} 
                                            className={`subunit-block ${status}`}
                                            style={{ width: `${blockWidth}px` }}
                                        >
                                            <div className="subunit-block-tooltip">
                                                <div className="tooltip-title">{subunit.title}</div>
                                                <div className="tooltip-stat">{subunit.rule_ids.length} Rule{subunit.rule_ids.length !== 1 ? 's' : ''}</div>
                                                {subunit.quiz_ids.length > 0 && <div className="tooltip-stat">{subunit.quiz_ids.length} Quiz{subunit.quiz_ids.length !== 1 ? 'zes' : ''}</div>}
                                            </div>
                                        </div>
                                    );

                                    const isTargetForTooltip = subject.id === 'torts' && index === 0;

                                    return isTargetForTooltip ? (
                                        <OnboardingTooltip 
                                            key={index}
                                            id="hint_library_blocks" 
                                            seenHints={seenHints} 
                                            onDismiss={onDismissHint} 
                                            position="bottom"
                                            text="Each block scales based on how many rules it contains. Darker colors mean higher mastery."
                                        >
                                            {blockItem}
                                        </OnboardingTooltip>
                                    ) : blockItem;
                                })}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

             <div className="library-legend">
                <div className="legend-item"><span className="legend-swatch mastered"></span> Mastered</div>
                <div className="legend-item"><span className="legend-swatch in_progress"></span> In Progress</div>
                <div className="legend-item"><span className="legend-swatch not_started"></span> Not Started</div>
            </div>

            {isSearchModalOpen && (
                <div className="modal-overlay active" onClick={() => setIsSearchModalOpen(false)}>
                    <div className="command-center-modal brutalist-modal theme-aware" onClick={e => e.stopPropagation()}>
                        <header className="modal-header">
                            <h2 className="modal-title">Search Results</h2>
                            <button className="close-btn" onClick={() => setIsSearchModalOpen(false)}>&times;</button>
                        </header>
                        <div className="modal-body">
                            {searchResults.length > 0 ? (
                                searchResults.map(res => {
                                    const isLocked = isFree && !res.is_free_preview;
                                    return (
                                        <div 
                                            key={res.id} 
                                            className={`result-item ${isLocked ? 'locked' : ''}`} 
                                            onClick={() => handleResultClick(res)}
                                        >
                                            <span className="result-path">{subjectMapDerived[res.subject] || res.subject} › {res.displayPath?.join(' › ')}</span>
                                            <h3 className="keynote-rule-name">
                                                {res.ruleName}
                                                {isLocked && (
                                                    <span className="material-symbols-outlined lock-icon-sm" style={{fontSize: '0.9em', marginLeft: '8px', verticalAlign: 'middle', opacity: 0.6}}>lock</span>
                                                )}
                                            </h3>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="no-results">No matches found for "{searchTerm}"</div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LibraryView;